import { zodResolver } from '@hookform/resolvers/zod'
import {
  Crop,
  FileVideo,
  Globe,
  Link2,
  MessageSquare,
  Repeat,
  Scissors,
  Sparkles,
  Subtitles,
  Timer,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'

import { FormErrorSummary } from '@/components/feedback/FormErrorSummary'
import { JobProgressPanel } from '@/components/feedback/JobProgressPanel'
import { BackToFactoryLink } from '@/features/factory/components/BackToFactoryLink'
import { GenerationTimeHint } from '@/features/factory/components/GenerationTimeHint'
import { CaptionColorPicker } from '@/components/forms/CaptionStyleCarousel'
import { Button } from '@/components/ui/Button'
import { Segmented, type SegmentedOption } from '@/components/ui/Segmented'
import { useStartCortes } from '@/api/endpoints/factories'
import { useActiveChannelFromUrl } from '@/hooks/useActiveChannel'
import { cn } from '@/lib/cn'
import { LlmProviderPicker } from '../_shared/LlmProviderPicker'
import { CinemaBackground } from '../ViralShortsForm/CinemaBackground'
import styles from '../ViralShortsForm/ViralShortsForm.module.scss'
import { cortesSchema, FORM_DEFAULTS, toCortesInput, type CortesFormValues } from './payload'

const FIELD_LABELS: Record<string, string> = {
  source_url: 'URL do vídeo',
  source_path: 'Arquivo enviado',
  language: 'Idioma',
  max_clips: 'Máx. de cortes',
  min_clip_seconds: 'Duração mínima',
  max_clip_seconds: 'Duração máxima',
  reframe_mode: 'Reenquadramento',
  subtitle_style: 'Legenda',
  custom_prompt: 'Instruções',
  force: 'Forçar',
}

const SOURCE_TYPES: ReadonlyArray<SegmentedOption<'url' | 'upload'>> = [
  { value: 'url', label: 'URL' },
  { value: 'upload', label: 'Arquivo' },
]

const LANGUAGES: ReadonlyArray<SegmentedOption<'pt-br' | 'en'>> = [
  { value: 'pt-br', label: 'PT-BR' },
  { value: 'en', label: 'EN' },
]

type ReframeKey = 'center' | 'face_track' | 'smart'
const REFRAME_MODES: ReadonlyArray<SegmentedOption<ReframeKey>> = [
  { value: 'smart', label: 'Inteligente' },
  { value: 'face_track', label: 'Rosto' },
  { value: 'center', label: 'Centro' },
]

const SUBTITLE_STYLES: ReadonlyArray<SegmentedOption> = [
  { value: 'solid', label: 'Sólida' },
  { value: 'neon_stroke', label: 'Neon' },
  { value: 'shadow_pop', label: 'Sombra' },
  { value: 'boxed', label: 'Caixa' },
  { value: 'karaoke', label: 'Karaokê' },
]

export function CortesForm() {
  const { active } = useActiveChannelFromUrl()
  const channel = active?.slug ?? ''

  const startMutation = useStartCortes()
  const [activeJobId, setActiveJobId] = useState<string | null>(null)
  const [advancedOpen, setAdvancedOpen] = useState(false)

  const form = useForm<CortesFormValues>({
    resolver: zodResolver(cortesSchema),
    defaultValues: FORM_DEFAULTS,
  })

  const sourceType = form.watch('source_type')
  const language = form.watch('language')
  const reframeMode = form.watch('reframe_mode')
  const subtitleStyle = form.watch('subtitle_style')
  const subtitleActiveColor = form.watch('subtitle_active_color')
  const force = form.watch('force')
  const llmProvider = form.watch('llm_provider')

  const onSubmit = form.handleSubmit((values) => {
    const payload = toCortesInput(values, channel)
    startMutation.mutate(payload, {
      onSuccess: (data) => {
        setActiveJobId(data.job_id)
      },
    })
  })

  if (activeJobId) {
    return (
      <div className={styles.panelShell}>
        <JobProgressPanel jobId={activeJobId} />
        <div className={styles.panelActions}>
          <Button
            variant="secondary"
            onClick={() => {
              setActiveJobId(null)
              form.reset(FORM_DEFAULTS)
            }}
          >
            Cortar outro
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.shell}>
      <CinemaBackground />
      <div className={styles.content}>
        <header className={styles.header}>
          <div className={styles.kicker}>
            <Scissors aria-hidden /> Cortes de podcast
          </div>
          <h1 className={styles.title}>Um episódio longo vira vários Shorts.</h1>
          <p className={styles.subtitle}>
            Cole a URL do episódio (ou aponte um arquivo já enviado). A gente transcreve, a IA
            escolhe os melhores trechos e cada um sai em 9:16 com legenda queimada.
          </p>
        </header>

        <BackToFactoryLink />

        <form className={styles.glass} onSubmit={onSubmit} noValidate>
          {startMutation.isError ? (
            <FormErrorSummary error={startMutation.error} fieldLabels={FIELD_LABELS} />
          ) : null}

          <div className={styles.rows}>
            <Row icon={<FileVideo />} label="Fonte">
              <Segmented<'url' | 'upload'>
                options={SOURCE_TYPES}
                value={sourceType}
                onValueChange={(v) => {
                  form.setValue('source_type', v)
                }}
                aria-label="Tipo de fonte"
              />
            </Row>

            {sourceType === 'url' ? (
              <Row icon={<Link2 />} label="URL do vídeo" htmlFor="cortes-url">
                <input
                  id="cortes-url"
                  type="url"
                  inputMode="url"
                  autoComplete="url"
                  placeholder="https://www.youtube.com/watch?v=..."
                  className={cn(styles.textarea, form.formState.errors.source_url && styles.textareaError)}
                  {...form.register('source_url')}
                />
                {form.formState.errors.source_url ? (
                  <span className={styles.errorText}>{form.formState.errors.source_url.message}</span>
                ) : null}
              </Row>
            ) : (
              <Row icon={<FileVideo />} label="Arquivo enviado" htmlFor="cortes-path">
                <input
                  id="cortes-path"
                  type="text"
                  placeholder="/caminho/para/o/arquivo.mp4 (via ClipCreator)"
                  className={cn(styles.textarea, form.formState.errors.source_path && styles.textareaError)}
                  {...form.register('source_path')}
                />
                {form.formState.errors.source_path ? (
                  <span className={styles.errorText}>{form.formState.errors.source_path.message}</span>
                ) : null}
              </Row>
            )}

            <Row icon={<Globe />} label="Idioma">
              <Segmented<'pt-br' | 'en'>
                options={LANGUAGES}
                value={language}
                onValueChange={(v) => {
                  form.setValue('language', v)
                }}
                aria-label="Idioma falado"
              />
            </Row>

            <Row icon={<Scissors />} label="Máx. de cortes" htmlFor="cortes-max">
              <input
                id="cortes-max"
                type="number"
                min={1}
                max={20}
                className={cn(styles.textarea, form.formState.errors.max_clips && styles.textareaError)}
                {...form.register('max_clips', { valueAsNumber: true })}
              />
            </Row>

            {advancedOpen ? (
              <div className={styles.advanced}>
                <AdvSection icon={<Crop />} label="Reenquadramento 9:16">
                  <Segmented<ReframeKey>
                    options={REFRAME_MODES}
                    value={reframeMode}
                    onValueChange={(v) => {
                      form.setValue('reframe_mode', v)
                    }}
                    aria-label="Reenquadramento"
                  />
                </AdvSection>

                <AdvSection icon={<Subtitles />} label="Estilo da legenda">
                  <Segmented
                    options={SUBTITLE_STYLES}
                    value={subtitleStyle}
                    onValueChange={(v) => {
                      form.setValue('subtitle_style', v)
                    }}
                    aria-label="Estilo da legenda"
                  />
                  {subtitleStyle === 'karaoke' ? (
                    <CaptionColorPicker
                      value={subtitleActiveColor ?? ''}
                      onValueChange={(c) => {
                        form.setValue('subtitle_active_color', c)
                      }}
                    />
                  ) : null}
                </AdvSection>

                <AdvSection icon={<Timer />} label="Duração do corte (s)">
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      type="number"
                      min={5}
                      max={180}
                      aria-label="Duração mínima"
                      className={styles.textarea}
                      {...form.register('min_clip_seconds', { valueAsNumber: true })}
                    />
                    <span aria-hidden>—</span>
                    <input
                      type="number"
                      min={10}
                      max={300}
                      aria-label="Duração máxima"
                      className={cn(
                        styles.textarea,
                        form.formState.errors.max_clip_seconds && styles.textareaError,
                      )}
                      {...form.register('max_clip_seconds', { valueAsNumber: true })}
                    />
                  </div>
                  {form.formState.errors.max_clip_seconds ? (
                    <span className={styles.errorText}>
                      {form.formState.errors.max_clip_seconds.message}
                    </span>
                  ) : null}
                </AdvSection>

                <AdvSection icon={<MessageSquare />} label="Instruções para a seleção">
                  <textarea
                    rows={3}
                    maxLength={600}
                    placeholder="Ex: priorize histórias pessoais e hot takes; evite jabá."
                    className={styles.textarea}
                    {...form.register('custom_prompt')}
                  />
                </AdvSection>

                <AdvSection icon={<Repeat />} label="Fonte repetida">
                  <ToggleChip
                    icon={<Repeat />}
                    label="Cortar mesmo com outro job rodando nesta fonte"
                    active={force}
                    onClick={() => {
                      form.setValue('force', !force)
                    }}
                  />
                </AdvSection>

                <LlmProviderPicker
                  value={llmProvider ?? 'auto'}
                  onChange={(v) => {
                    form.setValue('llm_provider', v)
                  }}
                />
              </div>
            ) : null}
          </div>

          <footer className={styles.cta}>
            <button
              type="button"
              className={cn(styles.advToggle, advancedOpen && styles.advToggleOpen)}
              onClick={() => {
                setAdvancedOpen((o) => !o)
              }}
            >
              Ajuste fino
            </button>
            <div className={styles.ctaSpacer} />
            <GenerationTimeHint videoKind="cortes" />
            <button
              type="submit"
              className={styles.generateBtn}
              disabled={!channel || startMutation.isPending}
            >
              <Sparkles aria-hidden />
              {startMutation.isPending ? 'Iniciando...' : 'Gerar cortes'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  )
}

interface RowProps {
  icon: ReactNode
  label: ReactNode
  htmlFor?: string
  children: ReactNode
}

function Row({ icon, label, htmlFor, children }: RowProps) {
  return (
    <div className={styles.row}>
      {htmlFor ? (
        <label className={styles.rowLabel} htmlFor={htmlFor}>
          <span className={styles.rowIcon} aria-hidden>
            {icon}
          </span>
          {label}
        </label>
      ) : (
        <div className={styles.rowLabel}>
          <span className={styles.rowIcon} aria-hidden>
            {icon}
          </span>
          {label}
        </div>
      )}
      <div className={styles.rowControl}>{children}</div>
    </div>
  )
}

interface AdvSectionProps {
  icon: ReactNode
  label: ReactNode
  children: ReactNode
}

function AdvSection({ icon, label, children }: AdvSectionProps) {
  return (
    <section className={styles.advSection}>
      <div className={styles.advSectionLabel}>
        <span className={styles.rowIcon} aria-hidden>
          {icon}
        </span>
        {label}
      </div>
      {children}
    </section>
  )
}

interface ToggleChipProps {
  icon: ReactNode
  label: string
  active: boolean
  onClick: () => void
}

function ToggleChip({ icon, label, active, onClick }: ToggleChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(styles.advToggle, active && styles.advToggleOpen)}
      aria-pressed={active}
    >
      <span className={styles.rowIcon} aria-hidden>
        {icon}
      </span>
      {label}
    </button>
  )
}
